import { FormControlLabel, Tooltip } from "@mui/material";
import MuiCheckbox from "@mui/material/Checkbox";
import { useField, useFormikContext } from "formik";
import type { ChangeEvent, ComponentProps, ReactNode } from "react";
import { useCallback } from "react";

import { useFormContext } from "../FormContext";
import { useFieldContext } from "../FormFieldContext";
import { useFieldLabelAndDescription } from "./helpers";

type CheckboxProps = ComponentProps<typeof MuiCheckbox> & {
  label: ReactNode;
  description?: ReactNode;
  ["data-test"]?: string;
};

export const Checkbox = ({ label, description, "data-test": dataTest, ...fieldProps }: CheckboxProps) => {
  return (
    <Tooltip title={description}>
      <FormControlLabel
        control={
          <MuiCheckbox
            {...fieldProps}
            size="medium"
            slotProps={{
              input: {
                "data-test": `${dataTest ?? fieldProps.name}-checkbox`,
              } as any,
            }}
          />
        }
        data-test={`${dataTest ?? fieldProps.name}-label`}
        label={label}
        slotProps={{
          typography: {
            style: { fontSize: "inherit" },
          },
        }}
      />
    </Tooltip>
  );
};

type FieldCheckboxProps = Omit<CheckboxProps, "id" | "name" | "checked" | "label" | "description">;

export const FieldCheckbox = (props: FieldCheckboxProps) => {
  const { submitOnBlur } = useFormContext();
  const { submitForm } = useFormikContext();

  const { controlId, name, dataTest } = useFieldContext();
  const [{ onChange, value }] = useField<boolean>(name);
  const [label, description] = useFieldLabelAndDescription(name);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      props.onChange?.(e, checked);
      onChange(e);
      if (submitOnBlur) {
        void submitForm();
      }
    },
    [onChange, props, submitForm, submitOnBlur],
  );

  return (
    <Checkbox
      {...props}
      id={controlId}
      name={name}
      checked={value}
      onChange={handleChange}
      label={label}
      description={description}
      data-test={dataTest}
    />
  );
};
